import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { List, Calendar as CalendarIcon, Download, Plus, Menu } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import BookingList from './BookingsList';
import BookingCalendar from './BookingsCalendar';
import CancelledBookings from './CancelledBookings';
import TentativeBookings from './TentativeBookings';
import AddBookingForm from './AddBookingForm';

export default function Bookings() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showAddBookingForm, setShowAddBookingForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Parse current tab from URL
  const searchParams = new URLSearchParams(location.search);
  const currentTab = searchParams.get('tab') || 'list';
  const view = searchParams.get('view') || 'list';

  const handleTabChange = (tab) => {
    if (tab === 'calendar') {
      navigate('/bookings?view=calendar');
    } else if (tab === 'cancelled') {
      navigate('/bookings?tab=cancelled');
    } else if (tab === 'tentative') {
      navigate('/bookings?tab=tentative');
    } else {
      navigate('/bookings');
    }
  };

  const handleAddBooking = () => {
    setShowAddBookingForm(true);
  };

  const handleBookingCreated = () => {
    setShowAddBookingForm(false);
    setRefreshTrigger((prev) => prev + 1);
  };

  const renderContent = () => {
    if (view === 'calendar') {
      return (
        <BookingCalendar
          onAddBooking={handleAddBooking}
          onSelectBooking={setSelectedBooking}
        />
      );
    }
    if (currentTab === 'cancelled') return <CancelledBookings />;
    if (currentTab === 'tentative') return <TentativeBookings />;
    return (
      <BookingList onAddBooking={handleAddBooking} refreshTrigger={refreshTrigger} />
    );
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Sidebar — receives mobile open state & close handler */}
      <Sidebar
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* ── Mobile top bar ── */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 sticky top-0 z-30">
          <button
            className="p-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
            onClick={() => setMobileSidebarOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          <img
            src="/logo.png"
            alt="Optimus Logo"
            className="h-7 w-auto object-contain mr-5"
          />

          
        </header>

        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-4 md:p-6 h-full">
            {/* Page heading row */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Bookings</h1>
                <p className="text-sm text-gray-500 mt-1">All Booking Details</p>
              </div>

              
            </div>

            {/* Tab Navigation — horizontally scrollable on mobile */}
            <div className="mb-6 overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0">
              <div className="flex items-center border-b border-gray-200 min-w-max md:min-w-0">
                <button
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    currentTab === 'list' && view !== 'calendar'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                  onClick={() => handleTabChange('list')}
                >
                  <List className="w-4 h-4" />
                  List View
                </button>
                <button
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    view === 'calendar'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                  onClick={() => handleTabChange('calendar')}
                >
                  <CalendarIcon className="w-4 h-4" />
                  Calendar View
                </button>
                <button
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    currentTab === 'cancelled'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                  onClick={() => handleTabChange('cancelled')}
                >
                  Cancelled Bookings
                </button>
                <button
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    currentTab === 'tentative'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                  onClick={() => handleTabChange('tentative')}
                >
                  Tentative Bookings
                </button>
              </div>
            </div>

            {/* Content Area */}
            {renderContent()}
          </div>
        </main>
      </div>

      {/* Add Booking Modal */}
      {showAddBookingForm && (
        <AddBookingForm
          onClose={() => setShowAddBookingForm(false)}
          onBookingCreated={handleBookingCreated}
        />
      )}
    </div>
  );
}